import { useCallback, useRef, useState } from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Ruler guides: the controlled (`guides` prop) or internal guide list and its updater, plus the
 * local positions shown while existing guides are being dragged (`use-guide-drag`).
 */
export function useGuidesState({
  externalGuides,
  onGuidesChange,
}: {
  externalGuides: CenterCanvasConfig['guides']
  onGuidesChange: CenterCanvasConfig['onGuidesChange']
}) {
  // Guides: external state with internal fallback
  const [internalGuides, setInternalGuides] = useState<Guide[]>([])
  const guides = externalGuides ?? internalGuides
  const updateGuides = useCallback(
    (next: Guide[] | ((prev: Guide[]) => Guide[])) => {
      if (onGuidesChange) {
        const resolved = typeof next === 'function' ? next(guides) : next

        onGuidesChange(resolved)
      } else {
        setInternalGuides(next)
      }
    },
    [onGuidesChange, guides]
  )

  const guidesRef = useRef(guides)

  guidesRef.current = guides

  // Local guide positions used during drag so the parent isn't updated on every pointer move.
  // The parent (and API) is notified only on mouse release via updateGuides.
  const [localDragGuides, setLocalDragGuides] = useState<Guide[] | null>(null)
  const localDragGuidesRef = useRef<Guide[] | null>(null)
  const updateGuidesRef = useRef(updateGuides)

  updateGuidesRef.current = updateGuides

  return {
    guides,
    updateGuides,
    guidesRef,
    localDragGuides,
    setLocalDragGuides,
    localDragGuidesRef,
    updateGuidesRef,
  }
}
